import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

interface QuoteItem {
  articleNumber?: string;
  description: string;
  quantity: number;
  price: number;
  discount?: {
    type: 'percentage' | 'amount';
    value: number;
    description?: string;
  };
  isRot?: boolean;
  isRut?: boolean;
}

interface QuoteItemsProps {
  items: QuoteItem[];
  onChange: (items: QuoteItem[]) => void;
}

const QuoteItems: React.FC<QuoteItemsProps> = ({ items, onChange }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<QuoteItem>({
    description: '',
    quantity: 1,
    price: 0,
    isRot: false,
    isRut: false
  });
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasIncompleteData, setHasIncompleteData] = useState(false);

  // Kontrollera om användaren har börjat fylla i något fält
  useEffect(() => {
    const hasAnyData = Boolean(
      (newItem.articleNumber && newItem.articleNumber.trim() !== '') ||
      (newItem.description && newItem.description.trim() !== '') ||
      (newItem.quantity && newItem.quantity !== 1) ||
      (newItem.price && newItem.price !== 0) ||
      (newItem.discount && newItem.discount.value > 0) ||
      newItem.isRot || newItem.isRut
    );
    // Kontrollera om posten är komplett
    const isComplete = Boolean(
      (newItem.description && newItem.description.trim() !== '') &&
      newItem.quantity > 0 &&
      newItem.price > 0
    );
    // Sätt bara hasIncompleteData till true om det finns ofullständig data
    if (hasAnyData && !isComplete) {
      setHasIncompleteData(true);
    }
  }, [newItem]);

  // Lägg till varning när användaren försöker lämna sidan med ofullständig data
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasIncompleteData) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasIncompleteData]);

  const handleAddItem = () => {
    // Validera alla fält
    if (!newItem.description) {
      setValidationError('Beskrivning måste anges');
      return;
    }
    if (!newItem.quantity || newItem.quantity <= 0) {
      setValidationError('Antal måste vara större än 0');
      return;
    }
    if (!newItem.price || newItem.price <= 0) {
      setValidationError('Pris måste vara större än 0');
      return;
    }

    // Om allt är OK, lägg till posten
    onChange([...items, newItem]);
    setNewItem({
      description: '',
      quantity: 1,
      price: 0,
      isRot: false,
      isRut: false
    });
    setValidationError(null);
    setHasIncompleteData(false);
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
    setNewItem({ ...items[index] });
  };

  const handleUpdateItem = () => {
    // Validera endast när man klickar på Uppdatera
    if (!newItem.description) {
      setValidationError('Beskrivning är obligatorisk');
      return;
    }
    if (!newItem.price) {
      setValidationError('Á-pris är obligatoriskt');
      return;
    }
    if (newItem.quantity <= 0) {
      setValidationError('Antal måste vara större än 0');
      return;
    }

    setValidationError(null);
    const updatedItems = [...items];
    updatedItems[editingIndex!] = { ...newItem };
    onChange(updatedItems);
    setEditingIndex(null);
    setNewItem({
      description: '',
      quantity: 1,
      price: 0,
      isRot: false,
      isRut: false
    });
    setHasIncompleteData(false);
  };

  const handleDeleteItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    onChange(updatedItems);
  };

  const calculateItemTotal = (item: QuoteItem) => {
    let total = item.quantity * item.price;
    if (item.discount) {
      if (item.discount.type === 'percentage') {
        total = total * (1 - item.discount.value / 100);
      } else {
        total = total - item.discount.value;
      }
    }
    return total;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Artiklar
      </Typography>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {hasIncompleteData && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Du har en ofullständig post. Vänligen fyll i alla fält eller klicka på "Rensa" för att ta bort den.
          <Button
            size="small"
            onClick={() => {
              setNewItem({
                description: '',
                quantity: 1,
                price: 0,
                isRot: false,
                isRut: false
              });
              setHasIncompleteData(false);
            }}
            sx={{ ml: 2 }}
          >
            Rensa
          </Button>
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Art.nr</TableCell>
              <TableCell>Beskrivning</TableCell>
              <TableCell>Antal</TableCell>
              <TableCell>À-pris</TableCell>
              <TableCell>Rabatt</TableCell>
              <TableCell>ROT/RUT</TableCell>
              <TableCell>Belopp</TableCell>
              <TableCell>Åtgärder</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.articleNumber || '-'}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {item.description}
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setEditingIndex(index);
                        setNewItem({ ...item });
                        setDescriptionModalOpen(true);
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.price.toFixed(2)} kr</TableCell>
                <TableCell>
                  {item.discount ? (
                    item.discount.type === 'percentage' ? (
                      `${item.discount.value}%`
                    ) : (
                      `${item.discount.value} kr`
                    )
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {item.isRot ? 'ROT' : item.isRut ? 'RUT' : '-'}
                </TableCell>
                <TableCell>{calculateItemTotal(item).toFixed(2)} kr</TableCell>
                <TableCell>
                  <Tooltip title="Redigera">
                    <IconButton onClick={() => handleEditItem(index)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ta bort">
                    <IconButton onClick={() => handleDeleteItem(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <TextField
                  size="small"
                  value={newItem.articleNumber || ''}
                  onChange={(e) => setNewItem({ ...newItem, articleNumber: e.target.value })}
                  placeholder="Art.nr"
                />
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Beskrivning"
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => setDescriptionModalOpen(true)}
                  >
                    <DescriptionIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                  inputProps={{ min: 1 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={newItem.price || ''}
                  onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={newItem.discount?.value || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      setNewItem({
                        ...newItem,
                        discount: {
                          type: 'amount',
                          value: Math.max(0, value)
                        }
                      });
                    } else {
                      setNewItem({
                        ...newItem,
                        discount: undefined
                      });
                    }
                  }}
                  placeholder="Rabatt"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newItem.isRot}
                      onChange={(e) => {
                        setNewItem({
                          ...newItem,
                          isRot: e.target.checked,
                          isRut: e.target.checked ? false : newItem.isRut
                        });
                      }}
                    />
                  }
                  label="ROT"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newItem.isRut}
                      onChange={(e) => {
                        setNewItem({
                          ...newItem,
                          isRut: e.target.checked,
                          isRot: e.target.checked ? false : newItem.isRot
                        });
                      }}
                    />
                  }
                  label="RUT"
                />
              </TableCell>
              <TableCell>
                {calculateItemTotal(newItem).toFixed(2)} kr
              </TableCell>
              <TableCell>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={editingIndex !== null ? handleUpdateItem : handleAddItem}
                  >
                    {editingIndex !== null ? 'Uppdatera' : 'Lägg till'}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal för beskrivning */}
      <Dialog
        open={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingIndex !== null ? 'Redigera beskrivning' : 'Lägg till beskrivning'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Skriv beskrivning här..."
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionModalOpen(false)}>Avbryt</Button>
          <Button 
            onClick={() => {
              setDescriptionModalOpen(false);
              if (editingIndex !== null) {
                handleUpdateItem();
              }
            }}
            variant="contained"
            color="primary"
          >
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteItems; 